import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHotkeys } from '../../hooks/useHotkeys';
import {
  Dialog,
  DialogContent,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { 
  Search, 
  ArrowRight, 
  Receipt, 
  Wallet, 
  Users, 
  FileText, 
  ShoppingCart, 
  Package,
  Building2,
  Tag,
  Loader2,
  Command
} from 'lucide-react';
import { cn, formatCurrency } from '../../lib/utils';
import { searchAPI } from '../../lib/api';
import { useDebounce } from '../../hooks/useDebounce';

const CATEGORY_CONFIG = {
  payables: { 
    label: 'Contas a Pagar', 
    icon: Receipt, 
    color: 'text-red-500',
    bgColor: 'bg-red-50 dark:bg-red-950/30'
  },
  receivables: { 
    label: 'Contas a Receber', 
    icon: Wallet, 
    color: 'text-green-500',
    bgColor: 'bg-green-50 dark:bg-green-950/30'
  },
  entities: { 
    label: 'Clientes/Fornecedores', 
    icon: Users, 
    color: 'text-blue-500',
    bgColor: 'bg-blue-50 dark:bg-blue-950/30'
  },
  contracts: { 
    label: 'Contratos', 
    icon: FileText, 
    color: 'text-purple-500',
    bgColor: 'bg-purple-50 dark:bg-purple-950/30'
  },
  sales: { 
    label: 'Vendas', 
    icon: ShoppingCart, 
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-50 dark:bg-emerald-950/30'
  },
  purchases: { 
    label: 'Compras', 
    icon: Package, 
    color: 'text-orange-500',
    bgColor: 'bg-orange-50 dark:bg-orange-950/30'
  },
  accounts: { 
    label: 'Contas Bancárias', 
    icon: Building2, 
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-50 dark:bg-cyan-950/30'
  },
  categories: { 
    label: 'Categorias', 
    icon: Tag, 
    color: 'text-amber-500',
    bgColor: 'bg-amber-50 dark:bg-amber-950/30'
  },
};

const STATUS_BADGES = {
  pending: { label: 'Pendente', variant: 'secondary' },
  paid: { label: 'Pago', variant: 'success' },
  received: { label: 'Recebido', variant: 'success' },
  overdue: { label: 'Vencido', variant: 'destructive' },
  partial: { label: 'Parcial', variant: 'warning' },
  active: { label: 'Ativo', variant: 'success' },
  cancelled: { label: 'Cancelado', variant: 'destructive' },
};

export function GlobalSearch({ open, onOpenChange }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  const debouncedQuery = useDebounce(query, 300);
  
  // Flatten results for keyboard navigation
  const flatResults = results ? Object.entries(results)
    .filter(([key, value]) => Array.isArray(value) && value.length > 0)
    .flatMap(([category, items]) => items.map(item => ({ ...item, category })))
    : [];

  const performSearch = useCallback(async (searchQuery) => {
    if (searchQuery.length < 2) {
      setResults(null);
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await searchAPI.search(searchQuery);
      setResults(response.data);
      setSelectedIndex(0);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    performSearch(debouncedQuery);
  }, [debouncedQuery, performSearch]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setResults(null);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const handleSelect = (item) => {
    navigate(item.route);
    onOpenChange(false);
    setQuery('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && flatResults[selectedIndex]) {
      e.preventDefault();
      handleSelect(flatResults[selectedIndex]);
    }
  };

  const renderResultItem = (item, index, globalIndex) => {
    const config = CATEGORY_CONFIG[item.category] || {};
    const Icon = config.icon || FileText;
    const isSelected = globalIndex === selectedIndex;
    const status = STATUS_BADGES[item.status];
    
    return (
      <div
        key={`${item.category}-${item.id}`}
        className={cn(
          "flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all",
          "hover:bg-accent/50",
          isSelected && "bg-accent ring-1 ring-primary/20"
        )}
        onClick={() => handleSelect(item)}
        onMouseEnter={() => setSelectedIndex(globalIndex)}
      >
        <div className={cn("p-2 rounded-lg", config.bgColor)}>
          <Icon className={cn("h-4 w-4", config.color)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium text-sm truncate">
              {item.description || item.name || item.customer_name || item.supplier_name || item.trade_name}
            </p>
            {status && (
              <Badge variant={status.variant} className="text-[10px] px-1.5 py-0">
                {status.label}
              </Badge>
            )}
          </div>
          {(item.amount || item.total || item.balance) && (
            <p className="text-xs text-muted-foreground">
              {formatCurrency(item.amount || item.total || item.balance)}
              {item.due_date && ` • Venc: ${item.due_date}`}
            </p>
          )}
          {item.person_name && (
            <p className="text-xs text-muted-foreground">{item.person_name}</p>
          )}
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100" />
      </div>
    );
  };

  let globalIndex = 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] p-0 gap-0 overflow-hidden">
        {/* Search Input */}
        <div className="flex items-center gap-3 p-4 border-b">
          <Search className="h-5 w-5 text-muted-foreground flex-shrink-0" />
          <Input
            ref={inputRef}
            type="text"
            placeholder="Buscar contas, clientes, contratos..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="border-0 focus-visible:ring-0 p-0 h-auto text-base placeholder:text-muted-foreground/70"
            data-testid="global-search-input"
          />
          {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>

        {/* Results */}
        <ScrollArea className="max-h-[400px]">
          {!query && (
            <div className="p-6 text-center text-muted-foreground">
              <Command className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm font-medium">Busca Global</p>
              <p className="text-xs mt-1">
                Digite ao menos 2 caracteres para buscar em todos os módulos
              </p>
              <div className="flex items-center justify-center gap-2 mt-4 text-xs">
                <kbd className="px-2 py-1 bg-muted rounded text-muted-foreground">↑↓</kbd>
                <span>navegar</span>
                <kbd className="px-2 py-1 bg-muted rounded text-muted-foreground">Enter</kbd>
                <span>selecionar</span>
                <kbd className="px-2 py-1 bg-muted rounded text-muted-foreground">Esc</kbd>
                <span>fechar</span>
              </div>
            </div>
          )}

          {query && results && results.total_count === 0 && (
            <div className="p-6 text-center text-muted-foreground">
              <Search className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm font-medium">Nenhum resultado encontrado</p>
              <p className="text-xs mt-1">Tente buscar por outro termo</p>
            </div>
          )}

          {results && results.total_count > 0 && (
            <div className="p-2 space-y-4">
              {Object.entries(CATEGORY_CONFIG).map(([category, config]) => {
                const items = results[category];
                if (!items || items.length === 0) return null;
                
                const Icon = config.icon;
                const startIndex = globalIndex;
                
                return (
                  <div key={category}>
                    <div className="flex items-center gap-2 px-3 py-1.5">
                      <Icon className={cn("h-3.5 w-3.5", config.color)} />
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {config.label}
                      </span>
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 ml-auto">
                        {items.length}
                      </Badge>
                    </div>
                    <div className="space-y-1">
                      {items.map((item, index) => {
                        const result = renderResultItem(item, index, globalIndex);
                        globalIndex++;
                        return result;
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        {results && results.total_count > 0 && (
          <div className="flex items-center justify-between p-3 border-t bg-muted/30 text-xs text-muted-foreground">
            <span>{results.total_count} resultado(s) encontrado(s)</span>
            <span className="flex items-center gap-1">
              Pressione <kbd className="px-1.5 py-0.5 bg-background rounded border">Enter</kbd> para abrir
            </span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
